import React, { useState, useEffect, useCallback, useRef } from "react";
import { ReactTags, Tag, InputRendererProps, ListBoxRendererProps } from 'react-tag-autocomplete';
import { apiGet } from "../../../api";
import { Suggestion, SuggestionResponse } from "../enums";
import { getInputTypeForFieldType, isDateTimeOperator } from "./utils";
import { getDateTimeFieldProps } from "./DateTimeProps";


interface TagsInputProps {
    fieldType: string;
    operator: string;
    value: any;
    fieldData?: any;
    handleOnChange: (value: any) => void;
    isMany: boolean;
    suggestions?: Suggestion[];
    allowNewTag?: boolean;
    fetchSuggestions: boolean
    suggestionsCache: any

}

interface ValueTagsInputProps extends InputRendererProps {
    fieldType: string;
    operator: string;
    isLoading: boolean;
}

interface ListBoxProps extends ListBoxRendererProps {
    hasMorePages: boolean;
    isLoading: boolean;
    currentQuery: string;
    currentPage: number;
    setCurrentPage: (currentPage: number) => void;
    fetchTagSuggestions: (query: string, page: number) => Promise<void>;
}

const DefaultClassNames = {
    root: "react-tags",
    rootIsActive: "is-active",
    rootIsDisabled: "is-disabled",
    rootIsInvalid: "is-invalid",
    label: "react-tags__label",
    tagList: "react-tags__list",
    tagListItem: "react-tags__list-item",
    tag: "react-tags__tag",
    tagName: "react-tags__tag-name",
    comboBox: "react-tags__combobox",
    input: "react-tags__combobox-input w-100",
    listBox: "react-tags__listbox",
    option: "react-tags__listbox-option",
    optionIsActive: "is-active",
    highlight: "react-tags__listbox-option-highlight"
};


function isForeignKeyField(fieldType: string): boolean {
    return fieldType === 'ForeignKey' || fieldType === 'ManyToManyField';
}

const TagsInput = React.forwardRef<HTMLInputElement, ValueTagsInputProps>(
    ({ classNames, inputWidth, fieldType, operator, isLoading, ...inputProps }, ref) => {
        let extendProps = {};

        if (isDateTimeOperator(operator)) {
            extendProps = getDateTimeFieldProps(operator);
        }

        return (
            <div
                className="d-flex align-items-center position-relative"
            >
                <input
                    {...inputProps}
                    {...extendProps}
                    ref={ref}
                    type={getInputTypeForFieldType(fieldType)}
                    className={classNames.input}
                    style={{ width: inputWidth }}
                />
                {isLoading && (
                    <div
                        className="d-flex align-items-center position-absolute"
                        style={{ right: "10px" }}
                    >
                        <div
                            className="spinner-border spinner-border-sm text-secondary"
                            title="Загрузка подсказок"
                            role="status"
                        >
                        </div>
                    </div>
                )}
            </div>
        );
    }
);


function useDebounce<T>(value: T, delay: number): T {
    const [debouncedValue, setDebouncedValue] = useState<T>(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);

    return debouncedValue;
}

function ListBox({
    hasMorePages,
    currentPage,
    setCurrentPage,
    currentQuery,
    fetchTagSuggestions,
    children,
    classNames,
    isLoading,
    ...listBoxProps
}: ListBoxProps) {

    const isLoadingRef = useRef(false);

    const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
        const target = e.currentTarget;
        const scrollTop = target.scrollTop;
        const scrollHeight = target.scrollHeight;
        const clientHeight = target.clientHeight;

        // Check if there's no scroll left (we're at the bottom)
        if (scrollHeight - scrollTop - clientHeight <= 1) {
            if (hasMorePages && !isLoading && !isLoadingRef.current) {
                isLoadingRef.current = true; // Устанавливаем флаг, что запрос в процессе
                fetchTagSuggestions(currentQuery, currentPage + 1)
                    .finally(() => {
                        // Сбрасываем флаг после завершения запроса
                        isLoadingRef.current = false;
                    });
            }
        }
    }, [hasMorePages, currentQuery, currentPage, fetchTagSuggestions, isLoading]);

    return (
        <div
            className={classNames.listBox}
            {...listBoxProps}
            onScroll={handleScroll}
            style={{
                overflowY: 'auto',
                minWidth: 'max-content',
                maxWidth: "500px"
            }}
        >
            {children}
        </div>
    );
}

export const TagsInputField: React.FC<TagsInputProps> = ({
    fieldType,
    operator,
    value,
    fieldData,
    handleOnChange,
    isMany,
    allowNewTag = true,
    suggestions,
    fetchSuggestions = true,
    suggestionsCache,
}) => {
    // State for tags, suggestions and pagination
    const [tags, setTags] = useState<Tag[]>([]);
    const [tagSuggestions, setTagSuggestions] = useState<Suggestion[]>(suggestions || []);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [hasMorePages, setHasMorePages] = useState<boolean>(false);
    const [currentQuery, setCurrentQuery] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);

    // Ref to track if component is mounted
    const isMounted = useRef(true);

    // Ref to track current request
    const currentRequestRef = useRef<string | null>(null);


    // Debounced query
    const [searchQuery, setSearchQuery] = useState('');
    const debouncedQuery = useDebounce(searchQuery, 500);

    // Fetch tag suggestions from API
    const fetchTagSuggestions = useCallback(async (query: string, page: number = 1): Promise<void> => {
        if (suggestions || !fetchSuggestions) return;

        const cacheKey = `${fieldData.name}_${query}`;
        const cachedEntry = suggestionsCache.current.get(cacheKey);

        if (cachedEntry?.isLoading) return;

        if (page === 1 && cachedEntry?.suggestions?.length) {
            setTagSuggestions(cachedEntry.suggestions);
            setHasMorePages(cachedEntry.hasMorePages);
            setCurrentPage(cachedEntry.lastPage);
            return;
        }

        suggestionsCache.current.set(cacheKey, {
            ...(cachedEntry || {}),
            isLoading: true,
        });

        const requestId = `${query}_${page}_${Date.now()}`;
        currentRequestRef.current = requestId;

        setIsLoading(true);
        try {
            const data: SuggestionResponse = await apiGet("table_filter_choices/", {
                field_path: fieldData.full_path,
                search: query,
                page: page
            });

            if (!isMounted.current || currentRequestRef.current !== requestId) return;

            // @ts-ignore
            const suggestionsFromResponse: Suggestion[] = data.results;

            setHasMorePages(data.has_next);
            setCurrentPage(page);

            if (page === 1) {
                setTagSuggestions(suggestionsFromResponse);

                suggestionsCache.current.set(cacheKey, {
                    suggestions: suggestionsFromResponse,
                    lastPage: 1,
                    hasMorePages: data.has_next,
                    isLoading: false,
                });
            } else {
                setTagSuggestions(prev => [...prev, ...suggestionsFromResponse]);

                if (cachedEntry) {
                    suggestionsCache.current.set(cacheKey, {
                        ...cachedEntry,
                        suggestions: [...cachedEntry.suggestions, ...suggestionsFromResponse],
                        lastPage: page,
                        hasMorePages: data.has_next,
                        isLoading: false,
                    });
                }
            }
        } catch (error) {
            console.error("Error fetching suggestions:", error);
            if (isMounted.current && currentRequestRef.current === requestId) {
                setTagSuggestions([]);
            }
            suggestionsCache.current.set(cacheKey, {
                ...(cachedEntry || {}),
                isLoading: false,
            });
        } finally {
            if (isMounted.current && currentRequestRef.current === requestId) {
                setIsLoading(false);
            }
        }
    }, [suggestions, fieldData?.full_path, fieldData?.name, fetchSuggestions, suggestionsCache]);

    // Handle tag selection
    const handleTagSelect = useCallback((tag: Tag) => {
        const newTags = isMany ? [...tags, tag] : [tag];
        setTags(newTags);
        if (newTags) {
            if (isMany) {
                handleOnChange(newTags);
            } else {
                handleOnChange(newTags[0]);
            }
        }
    }, [tags, handleOnChange, isMany]);

    // Handle tag deletion
    const handleTagDelete = useCallback((tagIndex: number) => {
        const newTags = tags.filter((_, i) => i !== tagIndex);
        setTags(newTags);

        if (newTags) {
            if (isMany) {
                handleOnChange(newTags);
            } else {
                handleOnChange(newTags[0]);
            }
        }
    }, [tags, handleOnChange, isMany]);

    // Handle input change for tag search
    const handleTagInputChange = useCallback((value: string) => {
        setCurrentQuery(value);
        setSearchQuery(value);
    }, []);

    // Effect to fetch suggestions when debounced query changes
    useEffect(() => {
        if (debouncedQuery) {
            setCurrentPage(1);
            fetchTagSuggestions(debouncedQuery, 1);
        }
    }, [debouncedQuery, fetchTagSuggestions]);

    // Initialize tags from value
    useEffect(() => {
        if (Array.isArray(value)) {
            setTags(value);
        } else if (value) {
            setTags([value]);
        } else {
            setTags([]);
        }
    }, [value]);

    // Effect to clear suggestions when field changes
    useEffect(() => {
        if (fieldData?.name) {
            setTagSuggestions([]);
            if (suggestions) {
                setTagSuggestions(suggestions);
            }
        }
    }, [fieldData?.name, suggestions, fetchTagSuggestions]);

    // Effect to cleanup on unmount
    useEffect(() => {
        return () => {
            isMounted.current = false;
        };
    }, []);

    return (
        <div className="react-tags-wrapper">
            <ReactTags
                selected={tags}
                suggestions={tagSuggestions}
                onAdd={handleTagSelect}
                onDelete={handleTagDelete}
                onInput={handleTagInputChange}
                allowNew={allowNewTag || !isForeignKeyField(fieldType)}
                classNames={DefaultClassNames}
                newOptionText="%value%"
                newOptionPosition="first"
                activateFirstOption={true}
                delimiterKeys={["Enter", ","]}
                noOptionsText="Нет совпадений"
                placeholderText={"Введите значение"}
                allowBackspace={true}
                onFocus={() => {
                    if (isLoading) return;
                    fetchTagSuggestions(currentQuery || "", 1);
                }}
                renderInput={(props: InputRendererProps) => (
                    <TagsInput fieldType={fieldType} operator={operator} isLoading={isLoading} {...props} />
                )}
                renderListBox={(props: ListBoxRendererProps) => (
                    <ListBox
                        isLoading={isLoading}
                        hasMorePages={hasMorePages}
                        currentPage={currentPage}
                        currentQuery={currentQuery}
                        fetchTagSuggestions={fetchTagSuggestions}
                        setCurrentPage={setCurrentPage}
                        {...props}
                    />
                )}
            />
        </div>
    );
};
